import { t } from '@/shared/lib/i18n'
import type { WeekdaySchedule } from '@/shared/ui/weekly-schedule-grid'

export const US_PHONE_CODE = '+1'
// At least one digit required — formatting-only values like "()--" are not phone numbers.
// Keep character-class + length limits in sync with Site model (parity tested in site_test).
export const US_PHONE_NUMBER_PATTERN = /^(?=.*[0-9])[0-9()\-\s]+$/
export const US_PHONE_NUMBER_MAX_LENGTH = 14
export const INTERNATIONAL_PHONE_NUMBER_PATTERN = /^[0-9]+$/
export const INTERNATIONAL_PHONE_NUMBER_MAX_LENGTH = 15

export interface SiteFormValidationData {
  name: string
  address: string
  city: string
  state: string
  zip: string
  phoneNumber: string
  followParentSchedule: boolean
  customDaySchedule: WeekdaySchedule
  timezoneId: number | null
  countryId: number | null
  phoneCode: string
}

export type SiteFormClientErrors = Partial<Record<keyof SiteFormValidationData, string>>

export const phoneNumberMaxLengthForCode = (phoneCode: string): number =>
  phoneCode === US_PHONE_CODE ? US_PHONE_NUMBER_MAX_LENGTH : INTERNATIONAL_PHONE_NUMBER_MAX_LENGTH

export const sanitizePhoneNumberInput = (phoneCode: string, rawValue: string): string => {
  const sanitized =
    phoneCode === US_PHONE_CODE ? rawValue.replace(/[^\d()\-\s]/g, '') : rawValue.replace(/\D/g, '')

  return sanitized.slice(0, phoneNumberMaxLengthForCode(phoneCode))
}

export const phoneNumberFormatError = (phoneCode: string, phoneNumber: string): string | null => {
  if (phoneCode === US_PHONE_CODE) {
    if (
      US_PHONE_NUMBER_PATTERN.test(phoneNumber) &&
      phoneNumber.length <= US_PHONE_NUMBER_MAX_LENGTH
    ) {
      return null
    }

    return t('organization.sites.form.phoneNumberInvalidUs')
  }

  if (
    INTERNATIONAL_PHONE_NUMBER_PATTERN.test(phoneNumber) &&
    phoneNumber.length <= INTERNATIONAL_PHONE_NUMBER_MAX_LENGTH
  ) {
    return null
  }

  return t('organization.sites.form.phoneNumberInvalidInternational')
}

export const trimSiteFormData = (data: SiteFormValidationData): SiteFormValidationData => ({
  ...data,
  name: data.name.trim(),
  address: data.address.trim(),
  city: data.city.trim(),
  state: data.state.trim(),
  zip: data.zip.trim(),
  phoneNumber: data.phoneNumber.trim(),
  phoneCode: data.phoneCode.trim(),
})

export const buildClientErrors = (data: SiteFormValidationData): SiteFormClientErrors => {
  const trimmed = trimSiteFormData(data)
  const clientErrors: SiteFormClientErrors = {}

  if (!trimmed.name) {
    clientErrors.name = t('organization.sites.form.nameRequired')
  }
  if (!trimmed.address) {
    clientErrors.address = t('organization.sites.form.addressRequired')
  }
  if (!trimmed.city) {
    clientErrors.city = t('organization.sites.form.cityRequired')
  }
  if (!trimmed.state) {
    clientErrors.state = t('organization.sites.form.stateRequired')
  }
  if (!trimmed.zip) {
    clientErrors.zip = t('organization.sites.form.zipRequired')
  }
  // Phone code/number are optional (REBUILD-108 UAT). Format-check only when both are present.
  if (trimmed.phoneNumber && trimmed.phoneCode) {
    const formatError = phoneNumberFormatError(trimmed.phoneCode, trimmed.phoneNumber)
    if (formatError) {
      clientErrors.phoneNumber = formatError
    }
  }
  if (!trimmed.timezoneId) {
    clientErrors.timezoneId = t('organization.sites.form.timezoneRequired')
  }
  if (!trimmed.countryId) {
    clientErrors.countryId = t('organization.sites.form.countryRequired')
  }

  return clientErrors
}

export const firstErrorFieldId = (clientErrors: SiteFormClientErrors): string | null => {
  const fieldOrder: Array<keyof SiteFormValidationData> = [
    'name',
    'address',
    'city',
    'state',
    'zip',
    'phoneCode',
    'phoneNumber',
    'timezoneId',
    'countryId',
  ]

  return fieldOrder.find((field) => clientErrors[field]) ?? null
}
